import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  useUserBlockedPatch,
  useUserProfilePatch,
  useUserRolePatch,
  useUserVenues,
  useUsers,
} from '../../api/admin';
import type { UserDto, UserVenueDto } from '../../api/admin';
import { fetchVenueDirectory, patchVenue, VENUE_DIRECTORY_QUERY_KEY } from '../../api/venues';
import type { VenueDirectoryEntry } from '../../api/venues';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { roleLabel } from '../../auth/roles';
import type { Role } from '../../auth/roles';
import { Combobox } from '../../components/Combobox';
import { Drawer, ErrorState, Select, Spinner } from '../../components/ui';
import {
  DEPARTMENT_LABEL,
  departmentSelectOptions,
  DEPARTMENT_SHORT,
} from '../../departments';
import { asDepartment, type ManagerDepartment } from '../../departments';
import { useVenueDirectory } from '../quotas/dictionary';
import { venueComboboxOptions } from '../quotas/quotas-model';
import { formatDateTime } from '../dashboard/format';
import {
  EMAIL_CHANGE_WARN,
  LAST_ADMIN_BLOCK_HINT,
  LAST_ADMIN_DEMOTION_HINT,
  OWN_ACCOUNT_HINT,
  ROLE_OPTIONS,
  blockActionLabel,
  blockedLabel,
  curatorVenueCount,
  emailError,
  isLastAdminBlock,
  curatorSavedLinks,
  isLastAdminDemotion,
  isMe,
  managedVenueCount,
  parseUsers,
  userProfilePatchFrom,
  userLabel,
  venueLinkDraftAfterFailure,
  venueLinkFailureLabel,
  venueLinkIsDirty,
  venueLinkLabel,
  venueLinkPatchPlan,
  venueLinkProgressLabel,
  venueLinkSectionForRole,
  withVenue,
  withoutVenue,
} from './settings-model';
import type { VenueLinkIntent, VenueLinkKind } from './settings-model';

/** The answer of `GET /users/:id/venues` — the read the section stages from. */
interface UserVenuesRead {
  venues?: UserVenueDto[];
  curatorVenueIds?: number[];
  /** ADDITIVE (v1.9.31, T.Z138) — the curator links WITH names; absent on an older api. */
  curatorVenues?: Array<{ id: number; name: string }>;
}

/**
 * The RIGHT DRAWER of the users tab (T.X4) — the shared `Drawer` pattern the
 * venue card and the application profile use, opened by clicking a roster row.
 *
 * Five blocks, in the order an admin works through them:
 *   1. ПРОФИЛЬ — e-mail (editable since #25.2, wave 9), role, last login, venue
 *      counts (read-only facts) and the display-name field, saved on blur through
 *      the audited `PATCH /users/:id/profile`.
 *   2. РОЛЬ — the same select the row shows, so the last-admin 409 is handled in
 *      ONE place and reads the same on both surfaces.
 *   3. ДОСТУП — block / unblock. The api's `BlockedGuard` makes it bite on the
 *      account's NEXT request; the drawer only records the decision.
 *   4. THE LINK SECTION (T.Z22, wave 11) — ONE section, chosen by the role
 *      (A16): a МЕНЕДЖЕР sees «Площадки» (the `venue_managers` links), a
 *      КУРАТОР sees «Куратор площадок» (the `venue_curators` links), an АДМИН
 *      sees neither. The editor is a STAGED SET over the #6 directory: chips
 *      with a per-chip «×», additions through the combobox, and ONE save that
 *      turns the diff into per-venue `PATCH /venues/:id` replace-sets (#8
 *      `managerUserIds` / `curatorUserIds`) — the USER id goes INTO each
 *      venue's set, which is what fixes A15, and each venue keeps its other
 *      people, which is what fixes A17.
 *
 * The roster row this drawer was opened FROM is not the source of truth for the
 * facts: the live row from the shared cache is. A role change made on the table
 * while the drawer is open is therefore not overwritten by it, and every answered
 * write shows up here the moment the invalidation lands.
 */
export function UserDrawer({ user, onClose }: { user: UserDto; onClose: () => void }) {
  return (
    <Drawer open onClose={onClose} title={userLabel(user)} description={user.email}>
      <UserDrawerBody user={user} onClose={onClose} />
    </Drawer>
  );
}

function UserDrawerBody({ user, onClose }: { user: UserDto; onClose: () => void }) {
  const me = useAuth();
  const queryClient = useQueryClient();
  const roster = useUsers();
  const rows = useMemo(() => parseUsers(roster.data), [roster.data]);
  // The live row, not the snapshot the click handed over.
  const current = rows.find((candidate) => candidate.id === user.id) ?? user;

  const rolePatch = useUserRolePatch();
  const profilePatch = useUserProfilePatch();
  const blockedPatch = useUserBlockedPatch();
  const venuesQuery = useUserVenues(current.id);
  const directoryQuery = useVenueDirectory();

  const [nameDraft, setNameDraft] = useState(current.displayName ?? '');
  const [emailDraft, setEmailDraft] = useState(current.email);
  // ADDITIVE (T.Z42) — the staged department; `null` = «— без отдела». Reset when the
  // live row changes underneath the drawer, like the two drafts above.
  const [departmentDraft, setDepartmentDraft] = useState<ManagerDepartment | null>(asDepartment(current.department));
  // True from the first keystroke that moves the login address: the hint under the
  // field says so out loud, and the answered PATCH clears it.
  const [emailChanged, setEmailChanged] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNameDraft(current.displayName ?? '');
    setEmailDraft(current.email);
    setDepartmentDraft(asDepartment(current.department));
    setEmailChanged(false);
  }, [current.id, current.email, current.displayName, current.department]);

  // A16 — the ONE link section the role owns; an АДМИН gets none. Remounting the
  // section on (user, role) also drops a half-staged set that belongs to another
  // role than the one now on the row.
  const linkKind = venueLinkSectionForRole(current.role);
  const directory = useMemo(() => directoryQuery.data ?? [], [directoryQuery.data]);

  // The saved set of the section the role shows: manager links come named from
  // the read; curator links resolve their names through `curatorSavedLinks` —
  // the contract field first (v1.9.31, T.Z138), the directory second, «Площадка
  // N» last — so the labels no longer depend on the directory being complete.
  const savedLinks = useMemo<UserVenueDto[]>(() => {
    if (linkKind === 'curator') {
      return curatorSavedLinks(venuesQuery.data, directory).map((row) => ({ ...row }));
    }
    return (venuesQuery.data?.venues ?? []).map((venue) => ({ venueId: venue.venueId, venueName: venue.venueName }));
  }, [linkKind, venuesQuery.data, directory]);

  // Every link write moves the venue (its set), the roster (the counts) and the
  // journal — the same two roots the venue card invalidates, plus the read above.
  const onLinksChanged = () => {
    void queryClient.invalidateQueries({ queryKey: ['venues'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  };

  const saveRole = (next: Role) => {
    if (next === current.role) return; // a no-op select must not become a PATCH
    setError(null);
    if (isLastAdminDemotion(current, next, rows)) {
      setError(LAST_ADMIN_DEMOTION_HINT);
      return;
    }
    rolePatch.mutate(
      { userId: current.id, role: next },
      {
        onError: (cause) =>
          setError(
            cause instanceof ApiError && cause.is('VALIDATION', 400)
              ? ROLE_LINKS_HINT
              : cause instanceof Error
                ? cause.message
                : 'Не удалось сохранить роль.',
          ),
      },
    );
  };

  // #25.2 — ONE PATCH carries the editable keys, only the ones that moved. The
  // staged department travels too, so a select change followed by a name blur does
  // not lose either half.
  const saveProfile = () => {
    const patch = userProfilePatchFrom(current, {
      email: emailDraft,
      displayName: nameDraft,
      department: departmentDraft,
    });
    if (Object.keys(patch).length === 0) return;
    setError(null);
    profilePatch.mutate(
      { userId: current.id, patch },
      {
        onSuccess: (row) => {
          setNameDraft(row.displayName ?? '');
          setEmailDraft(row.email);
          setDepartmentDraft(asDepartment(row.department));
          setEmailChanged(false);
        },
        onError: (cause) =>
          setError(cause instanceof ApiError && cause.is('CONFLICT', 409) ? EMAIL_TAKEN_HINT : describeProfileError(cause)),
      },
    );
  };

  /**
   * ADDITIVE (T.Z42) — the «Отдел» select PATCHes on change, like the role select
   * above, with a ONE-KEY body: the department is its own audited move
   * (`ENTITY_CHANGE` with the old and new value), and a select that merely opened
   * must never look like a change. The api refuses (400) a department on a
   * non-МЕНЕДЖЕР, which is why the section renders for a МЕНЕДЖЕР only.
   */
  const saveDepartment = (next: ManagerDepartment | null) => {
    setDepartmentDraft(next);
    if (next === asDepartment(current.department)) return; // a no-op select must not become a PATCH
    setError(null);
    profilePatch.mutate(
      { userId: current.id, patch: { department: next } },
      {
        onError: (cause) =>
          setError(
            cause instanceof ApiError && cause.is('VALIDATION', 400)
              ? DEPARTMENT_ROLE_HINT
              : cause instanceof Error
                ? cause.message
                : 'Не удалось сохранить отдел.',
          ),
      },
    );
  };

  const toggleBlocked = () => {
    setError(null);
    const blocking = current.blocked !== true;
    if (blocking) {
      if (isMe(me.user?.email, current.email)) {
        setError(OWN_ACCOUNT_HINT);
        return;
      }
      if (isLastAdminBlock(current, rows)) {
        setError(LAST_ADMIN_BLOCK_HINT);
        return;
      }
    }
    blockedPatch.mutate(
      { userId: current.id, blocked: blocking },
      { onError: (cause) => setError(cause instanceof Error ? cause.message : 'Не удалось изменить доступ.') },
    );
  };

  const blocked = blockedLabel(current);

  return (
    <div
      className="user-drawer"
      data-testid="user-drawer"
      data-user-id={current.id}
      data-blocked={String(current.blocked === true)}
    >
      <section className="user-drawer__section" aria-label="Профиль">
        <dl className="user-drawer__facts">
          {/* A13 (owner UX pass, 2026-09-12): the sign-in PROVIDER is not displayed —
              every account signs in BOTH ways (D21), so «Яндекс/Код» said nothing true
              about the person any more. The role and the venue links are the facts. */}
          <div>
            <dt>Последний вход</dt>
            <dd data-testid="user-drawer-last-login">{formatDateTime(current.lastLoginAt)}</dd>
          </div>
          <div>
            <dt>Площадок</dt>
            <dd data-testid="user-drawer-venue-count">{managedVenueCount(current)}</dd>
          </div>
          <div>
            <dt>Куратор</dt>
            <dd data-testid="user-drawer-curator-count">{curatorVenueCount(current)}</dd>
          </div>
        </dl>

        <label className="user-drawer__field" htmlFor="user-drawer-email">
          <span className="user-drawer__label">E-mail</span>
          <span className="user-drawer__name-row">
            <input
              id="user-drawer-email"
              className="input"
              type="email"
              data-testid="user-drawer-email"
              value={emailDraft}
              placeholder={current.email}
              aria-invalid={emailError(emailDraft) !== undefined}
              onChange={(event) => {
                setEmailDraft(event.currentTarget.value);
                setEmailChanged(event.currentTarget.value.trim().toLowerCase() !== current.email.toLowerCase());
              }}
              onBlur={() => saveProfile()}
              onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()}
            />
            {profilePatch.isPending ? <Spinner label="Сохраняем" /> : null}
          </span>
          <span className="user-drawer__hint" data-testid="user-drawer-email-hint">
            {emailError(emailDraft) ??
              (emailChanged ? `Пользователь войдёт по адресу ${emailDraft.trim().toLowerCase()}.` : EMAIL_CHANGE_WARN)}
          </span>
        </label>

        <label className="user-drawer__field" htmlFor="user-drawer-name">
          <span className="user-drawer__label">Отображаемое имя</span>
          <span className="user-drawer__name-row">
            <input
              id="user-drawer-name"
              className="input"
              data-testid="user-drawer-name"
              value={nameDraft}
              placeholder={current.email}
              onChange={(event) => setNameDraft(event.currentTarget.value)}
              onBlur={() => saveProfile()}
              onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()}
            />
          </span>
          <span className="user-drawer__hint">
            Пусто — показываем e-mail. Сохранённое имя держится как псевдоним: следующий вход через Яндекс его не
            затрёт.
          </span>
        </label>
      </section>

      <section className="user-drawer__section" aria-label="Роль">
        <label className="user-drawer__field" htmlFor="user-drawer-role-select">
          <span className="user-drawer__label">Роль</span>
          <Select
            id="user-drawer-role-select"
            data-testid="user-drawer-role-select"
            value={current.role}
            disabled={rolePatch.isPending}
            onChange={(event) => saveRole(event.target.value as Role)}
          >
            {ROLE_OPTIONS.map((role) => (
              <option key={role} value={role}>
                {roleLabel(role)}
              </option>
            ))}
          </Select>
        </label>
      </section>

      {/* ADDITIVE (T.Z42) — the department of a МЕНЕДЖЕР. A МЕНЕДЖЕР-only section on
          purpose (the D21 rule the api enforces with its 400): a department is a
          manager fact, and showing the select on another role would invite a refusal. */}
      {current.role === 'MANAGER' ? (
        <section className="user-drawer__section" aria-label="Отдел">
          <label className="user-drawer__field" htmlFor="user-drawer-department">
            <span className="user-drawer__label">Отдел</span>
            <Select
              id="user-drawer-department"
              data-testid="user-drawer-department"
              value={departmentDraft ?? ''}
              disabled={profilePatch.isPending}
              onChange={(event) => saveDepartment(event.target.value === '' ? null : (event.target.value as ManagerDepartment))}
            >
              {departmentSelectOptions().map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </label>
          <span className="user-drawer__hint" data-testid="user-drawer-department-hint">
            {departmentDraft === null
              ? 'Без отдела: площадка менеджера не попадёт в фильтры «Отдел привлечения» и «Отдел сопровождения».'
              : `Отдел площадок менеджера: ${DEPARTMENT_LABEL[departmentDraft]} (${DEPARTMENT_SHORT[departmentDraft]}). Изменение видно в журнале аудита.`}
          </span>
        </section>
      ) : null}

      <section className="user-drawer__section" aria-label="Доступ">
        <div className="user-drawer__access">
          <button
            type="button"
            className={current.blocked ? 'btn' : 'btn btn--danger'}
            data-testid="user-drawer-block"
            disabled={blockedPatch.isPending}
            onClick={toggleBlocked}
          >
            {blockActionLabel(current)}
          </button>
          {blocked ? (
            <span className="badge badge--bad" data-testid="user-drawer-blocked-badge">
              {blocked}
            </span>
          ) : (
            <span className="user-drawer__hint">Блок снимает доступ со следующего запроса этого аккаунта.</span>
          )}
        </div>
        {current.blocked ? (
          <span className="user-drawer__hint" data-testid="user-drawer-blocked-note">
            Вход отклоняется, а действующая сессия перестаёт работать на первом же запросе.
          </span>
        ) : null}
      </section>

      {linkKind !== null ? (
        <VenueLinksSection
          key={`${current.id}:${current.role}`}
          kind={linkKind}
          userId={current.id}
          saved={savedLinks}
          directory={directory}
          loading={venuesQuery.isPending || directoryQuery.isPending}
          loadError={
            venuesQuery.isError || directoryQuery.isError ? 'Не удалось загрузить список площадок.' : null
          }
          onRetry={() => {
            void venuesQuery.refetch();
            void directoryQuery.refetch();
          }}
          onChanged={onLinksChanged}
        />
      ) : null}

      {error ? (
        <span className="user-role-error" role="alert" data-testid="user-drawer-error">
          {error}
        </span>
      ) : null}

      <p className="settings-note" data-testid="user-drawer-note">
        Роль, имя, e-mail, отдел, блокировку и площадки сервер пишет в журнал аудита (ENTITY_CHANGE).
      </p>
      <div className="user-drawer__footer">
        <button type="button" className="btn" data-testid="user-drawer-close" onClick={onClose}>
          Закрыть
        </button>
      </div>
    </div>
  );
}

const EMAIL_TAKEN_HINT = 'Этот адрес уже занят другим пользователем.';

/** The api's 400 on a department for a non-МЕНЕДЖЕР target (D21) — the UI restates it. */
const DEPARTMENT_ROLE_HINT = 'Отдел назначается только менеджеру — смените роль, чтобы указать отдел.';

/** The api's 400 on a role change whose account has no link of the new kind (D21). */
const ROLE_LINKS_HINT =
  'Роль не сохранена: аккаунту этой роли нужна хотя бы одна площадка соответствующей привязки.';

const MANAGER_LINK_HINT =
  'Площадки, где этот пользователь менеджер. Убирая строку, вы снимаете назначение только у него — площадка и её другие менеджеры остаются.';

const CURATOR_LINK_HINT = 'Площадки, где этот пользователь куратор: они определяют состав его кабинета.';

const MANAGER_EMPTY_HINT = 'Площадок нет — общие списки открываются без чужого фильтра.';

const CURATOR_EMPTY_HINT = 'Площадок куратора нет — кабинет этого пользователя пуст.';

/**
 * The ROLE-BASED link editor (T.Z22, wave 11 — A15/A16/A17). ONE component for
 * both kinds, because the mechanics are the same and only the wire key differs.
 *
 * The edit is STAGED: «×» on a chip and a combobox pick only move the draft, and
 * ONE «Сохранить» turns the diff into per-venue `PATCH /venues/:id` writes. Each
 * payload is the venue's CURRENT link set (read from a freshly fetched #6
 * directory, so a link another admin just made is not dropped) with THIS user
 * appended or dropped — which is the fix for A15 (the user id, never a venue id,
 * lands in `curatorUserIds`) and A17 (a removal writes the set minus the user,
 * instead of restating the user's own list). The writes run SEQUENTIALLY with a
 * progress caption; a refused venue is named and the rest stay applied, and a
 * retry re-plans from the refreshed read so only what is still missing travels.
 */
function VenueLinksSection({
  kind,
  userId,
  saved,
  directory,
  loading,
  loadError,
  onRetry,
  onChanged,
}: {
  kind: VenueLinkKind;
  userId: number;
  saved: ReadonlyArray<UserVenueDto>;
  directory: ReadonlyArray<VenueDirectoryEntry>;
  loading: boolean;
  loadError: string | null;
  onRetry: () => void;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const [staged, setStaged] = useState<UserVenueDto[] | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failed, setFailed] = useState<Array<{
    venueId: number;
    venueName: string;
    intent: VenueLinkIntent;
    message: string;
  }> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = staged ?? saved;
  const saving = progress !== null;
  const dirty = venueLinkIsDirty(
    saved.map((venue) => venue.venueId),
    list.map((venue) => venue.venueId),
  );

  // Every venue of the directory is a legal add (T.Z27's note: the online
  // filials included) — minus the ones the draft already holds.
  const options = useMemo(() => {
    const known = new Set(list.map((venue) => venue.venueId));
    return venueComboboxOptions(directory.filter((venue) => !known.has(venue.id)));
  }, [directory, list]);

  const clearFeedback = () => {
    setFailed(null);
    setError(null);
  };

  const stageRemove = (venueId: number) => {
    clearFeedback();
    setStaged(withoutVenue(list, venueId));
  };

  const stageAdd = (next: string) => {
    const venueId = Number.parseInt(next, 10);
    const entry = Number.isFinite(venueId) ? directory.find((venue) => venue.id === venueId) : undefined;
    if (entry === undefined) return;
    clearFeedback();
    setStaged(withVenue(list, { venueId: entry.id, venueName: entry.name }));
  };

  const save = async () => {
    if (!dirty || saving) return;
    clearFeedback();

    // The payloads fold each venue's CURRENT set in, so the save reads the
    // directory fresh: a stale cache would silently drop a link another admin
    // made a minute ago. A failed refresh cancels the save instead of guessing.
    let directoryNow = directory;
    try {
      directoryNow = await fetchVenueDirectory();
      queryClient.setQueryData(VENUE_DIRECTORY_QUERY_KEY, directoryNow);
    } catch {
      setError('Не удалось обновить список площадок — сохранение отменено, попробуйте ещё раз.');
      return;
    }

    const plan = venueLinkPatchPlan({
      kind,
      userId,
      directory: directoryNow,
      savedIds: saved.map((venue) => venue.venueId),
      stagedIds: list.map((venue) => venue.venueId),
    });
    // T.Z30 — a changed venue whose CURRENT set the fresh read does not carry is not
    // a guessable write: restating an unknown set would unlink everyone on it. The
    // save stops BEFORE the first request and says so loudly — never a silent no-op.
    if (plan.unresolved.length > 0) {
      const named = plan.unresolved.map((venue) => `«${venue.venueName}»`).join(', ');
      setError(
        `Не удалось прочитать текущие привязки: ${named}. Сохранение отменено, ничего не записано — обновите страницу и попробуйте ещё раз.`,
      );
      return;
    }
    if (plan.patches.length === 0) {
      // The read already matches the draft — the staged edits are a no-op.
      setStaged(null);
      return;
    }

    setProgress({ done: 0, total: plan.patches.length });
    const refused: Array<{
      venueId: number;
      venueName: string;
      intent: VenueLinkIntent;
      message: string;
    }> = [];
    let done = 0;
    for (const patch of plan.patches) {
      try {
        await patchVenue(patch.venueId, patch.body);
      } catch (cause) {
        refused.push({
          venueId: patch.venueId,
          venueName: patch.venueName,
          intent: patch.intent,
          message: cause instanceof Error ? cause.message : 'Не удалось записать привязку.',
        });
      }
      done += 1;
      setProgress({ done, total: plan.patches.length });
    }
    setProgress(null);

    onChanged();
    // The chips must show the state the writes just made — the refetched read,
    // not the stale draft. On a partial failure the refused intents stay staged
    // on top of that read, so the next save re-plans and writes only what is
    // still missing.
    await queryClient.refetchQueries({ queryKey: ['admin', 'users', userId, 'venues'] });
    if (refused.length === 0) {
      setStaged(null);
    } else {
      setFailed(refused);
      const read = queryClient.getQueryData<UserVenuesRead>(['admin', 'users', userId, 'venues']);
      const savedNow: UserVenueDto[] =
        kind === 'curator'
          ? curatorSavedLinks(read, directoryNow).map((row) => ({ ...row }))
          : read?.venues ?? [];
      setStaged(venueLinkDraftAfterFailure(savedNow, refused));
    }
  };

  return (
    <section
      className="user-drawer__section"
      aria-label={venueLinkLabel(kind)}
      data-testid="user-drawer-links"
      data-kind={kind}
    >
      <h3 className="user-drawer__heading">
        {venueLinkLabel(kind)} ({list.length})
      </h3>
      <p className="user-drawer__hint">{kind === 'manager' ? MANAGER_LINK_HINT : CURATOR_LINK_HINT}</p>

      {loading ? (
        <Spinner label="Загружаем площадки" />
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={onRetry} />
      ) : (
        <ul className="user-drawer__venues" data-testid="user-drawer-links-list">
          {list.length === 0 ? (
            <li className="user-drawer__venue user-drawer__venue--empty" data-testid="user-drawer-links-empty">
              {kind === 'manager' ? MANAGER_EMPTY_HINT : CURATOR_EMPTY_HINT}
            </li>
          ) : (
            list.map((venue) => (
              <li
                className="user-drawer__venue"
                key={venue.venueId}
                data-testid="user-drawer-links-venue"
                data-venue-id={venue.venueId}
              >
                <span className="user-drawer__venue-name" title={venue.venueName}>
                  {venue.venueName}
                </span>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={`Убрать ${venue.venueName}`}
                  data-testid={`user-drawer-links-remove-${venue.venueId}`}
                  disabled={saving}
                  onClick={() => stageRemove(venue.venueId)}
                >
                  ×
                </button>
              </li>
            ))
          )}
        </ul>
      )}

      <div className="user-drawer__add">
        <Combobox
          id="user-drawer-links-add"
          testId="user-drawer-links-add"
          value=""
          options={options}
          placeholder={`Добавить в «${venueLinkLabel(kind)}» — найдите по названию или адресу`}
          emptyText="Площадка не найдена"
          clearLabel="Очистить выбор площадки"
          ariaLabel={`Добавить площадку — ${venueLinkLabel(kind)}`}
          resultsLabel={(count) => `Найдено площадок: ${count}`}
          onChange={stageAdd}
        />
      </div>

      {dirty ? (
        <div className="user-drawer__save">
          <button
            type="button"
            className="btn btn--primary"
            data-testid="user-drawer-links-save"
            disabled={saving}
            onClick={() => void save()}
          >
            {saving ? venueLinkProgressLabel(progress?.done ?? 0, progress?.total ?? 1) : 'Сохранить привязки'}
          </button>
          <button
            type="button"
            className="btn"
            data-testid="user-drawer-links-reset"
            disabled={saving}
            onClick={() => {
              clearFeedback();
              setStaged(null);
            }}
          >
            Отменить
          </button>
        </div>
      ) : null}

      {failed !== null && failed.length > 0 ? (
        <span className="user-role-error" role="alert" data-testid="user-drawer-links-failures">
          {venueLinkFailureLabel(failed)} {failed[0]?.message}
        </span>
      ) : null}
      {error ? (
        <span className="user-role-error" role="alert" data-testid="user-drawer-links-error">
          {error}
        </span>
      ) : null}
    </section>
  );
}

/** The profile PATCH refusals that deserve their own sentence; the rest keep their message. */
function describeProfileError(error: unknown): string {
  if (error instanceof ApiError && error.is('VALIDATION', 400)) {
    return 'Адрес выглядит неполным — нужен адрес вида name@example.ru.';
  }
  return error instanceof Error ? error.message : 'Не удалось сохранить профиль.';
}
